import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Progress, Stack, Text, Tooltip } from '@mantine/core';

import { UI_COLORS } from 'constants/colors';
import {
  COMPLETE_BIO,
  DECISION_DASHBOARD,
  NUTRIENT_PANEL,
  PERFORMANCE_PANEL,
  productDisplayNames,
  productShortNames,
  ROOTWORM_PRESSURE_PANEL,
  SCN_PRESSURE_PANEL,
} from 'constants/products';

import { retrieveProgressStatuses } from 'util/orders';
import { hasLegacyProducts, hasProducts } from 'util/product';
import { capitalizeEveryWord } from 'util/stringUtils';
import { SamplePlanType } from 'store/samplePlans/types';

type ProgressStatusPropsType = {
  samplingPlan: SamplePlanType;
};

export const ProgressStatus = ({ samplingPlan }: ProgressStatusPropsType) => {
  const { t } = useTranslation();
  const {
    samplingStatus,
    analysisStatus,
    orderedStatus,
    sampledStatus,
    arrivedStatus,
    nutrientStatus,
    pressureStatus,
    performanceStatus,
  } = retrieveProgressStatuses(samplingPlan.sample_progress_statuses, t);

  const analysisList = hasLegacyProducts(samplingPlan)
    ? [
        {
          label: t(productShortNames[ROOTWORM_PRESSURE_PANEL]),
          value: pressureStatus,
          disabled: !hasProducts(samplingPlan, ROOTWORM_PRESSURE_PANEL, DECISION_DASHBOARD),
        },
        {
          label: t(productShortNames[SCN_PRESSURE_PANEL]),
          value: pressureStatus,
          disabled: !hasProducts(samplingPlan, SCN_PRESSURE_PANEL, DECISION_DASHBOARD),
        },
        {
          label: t(productShortNames[DECISION_DASHBOARD]),
          value: performanceStatus,
          disabled: !hasProducts(samplingPlan, DECISION_DASHBOARD),
        },
      ]
    : [
        {
          label: t(productShortNames[NUTRIENT_PANEL]),
          value: nutrientStatus,
          disabled: !hasProducts(samplingPlan, NUTRIENT_PANEL),
        },
        {
          label: productDisplayNames[COMPLETE_BIO],
          value: performanceStatus,
          disabled: !hasProducts(samplingPlan, PERFORMANCE_PANEL),
        },
        {
          label: t('emi'),
          value: samplingPlan.emi_status ?? 'N/A',
          disabled: !(samplingPlan.is_pro || samplingPlan.is_till_rx),
        },
        {
          label: t('adcp'),
          value: samplingPlan.adcp_status ?? 'N/A',
          disabled: !samplingPlan.is_till_rx,
        },
      ];

  const progressBars = [
    {
      label: t('ordered'),
      value: orderedStatus,
      color: UI_COLORS.darkBlue,
      progress: 100,
    },
    {
      label: t('sampled'),
      value: `${sampledStatus} / ${orderedStatus}`,
      color: UI_COLORS.darkBlue,
      progress: (100 * Number(sampledStatus)) / Number(orderedStatus),
    },
    {
      label: t('arrived'),
      value: `${arrivedStatus} / ${orderedStatus}`,
      color: UI_COLORS.darkBlue,
      progress: (100 * Number(arrivedStatus)) / Number(orderedStatus),
    },
  ];

  return (
    <Grid>
      <Grid.Col span={5}>
        <Stack gap={2}>
          <Grid>
            <Grid.Col span={6}>
              <Text size="xs" fw="bold" td="underline">
                {t('sampling')}:
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="xs">{samplingStatus}</Text>
            </Grid.Col>
          </Grid>
          {progressBars.map((progressBar) => (
            <Grid key={progressBar.label}>
              <Grid.Col span={6}>
                <Text size="xs" fw="bold">
                  {progressBar.label}:
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Tooltip
                  label={`${progressBar.label}: ${progressBar.value}`}
                  color={UI_COLORS.darkBlue}
                  position="right"
                >
                  <Progress.Root size="xl" w="5rem">
                    <Progress.Section value={progressBar.progress} />
                  </Progress.Root>
                </Tooltip>
              </Grid.Col>
            </Grid>
          ))}
        </Stack>
      </Grid.Col>
      <Grid.Col span={1} />
      <Grid.Col span={6}>
        <Grid>
          <Grid.Col span={4}>
            <Text size="xs" fw="bold" td="underline">
              {t('analysis')}:
            </Text>
          </Grid.Col>
          <Grid.Col span={8}>
            <Text size="xs">{analysisStatus}</Text>
          </Grid.Col>
        </Grid>
        <Stack gap={0}>
          {analysisList
            .filter((stat) => !stat.disabled)
            .map((stat) => (
              <Grid key={stat.label}>
                <Grid.Col span={4}>
                  <Text size="xs" fw="bold">
                    {stat.label}:
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs">{capitalizeEveryWord(String(stat.value))}</Text>
                </Grid.Col>
              </Grid>
            ))}
        </Stack>
      </Grid.Col>
    </Grid>
  );
};
